import { HttpErrorResponse, type HttpInterceptorFn, type HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppError } from '../error/app-error';
import { AUTH_ERROR_CODES } from '../models/auth.model';
import type { IResponse } from '../models/api-response.model';
import { AuthService, NO_AUTH_HEADER_URLS, SELF_HANDLED_401_URLS } from '../services/auth.service';

function isApiRequest(url: string): boolean {
  return url.startsWith(environment.apiBaseUrl);
}

function needsNoAuthHeader(url: string): boolean {
  return NO_AUTH_HEADER_URLS.some((path) => url.includes(path));
}

function ownsItsOwn401(url: string): boolean {
  return SELF_HANDLED_401_URLS.some((path) => url.includes(path));
}

function withAuth<T>(req: HttpRequest<T>, accessToken: string): HttpRequest<T> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${accessToken}` },
  });
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  if (!isApiRequest(req.url)) {
    return next(req);
  }

  const noAuthHeader = needsNoAuthHeader(req.url);
  const selfHandled401 = ownsItsOwn401(req.url);
  const accessToken = authService.accessToken;
  const authed = !noAuthHeader && accessToken ? withAuth(req, accessToken) : req;

  return next(authed).pipe(
    catchError((err: unknown) => {
      // Handle both HttpErrorResponse and AppError instances
      let statusCode: number;
      let errorCode: string | undefined;

      if (err instanceof HttpErrorResponse) {
        statusCode = err.status;
        const body = err.error as Partial<IResponse<unknown>> | undefined;
        errorCode = body?.errorCode;
      } else if (err instanceof AppError) {
        statusCode = err.statusCode;
        errorCode = err.code;
      } else {
        // Not a 401 error or unrecognized error type

        return throwError(() => err);
      }

      // Only handle 401 errors
      if (statusCode !== 401) {
        return throwError(() => err);
      }

      // These URLs own their own 401 handling — login/refresh/forgot/reset (unauthenticated, so
      // a 401 from them is never a stale-token situation) and change-password (authenticated, but
      // its 401 means "wrong currentPassword" and must render inline, not force a logout).
      if (selfHandled401) {
        return throwError(() => err);
      }

      // TOKEN_EXPIRED is the only recoverable 401: silently refresh and retry.
      // All REFRESH_TOKEN_* codes surface here as the failure of refresh() below
      // (POST /auth/refresh is a skipped URL), so they force logout via its catchError.
      if (errorCode === AUTH_ERROR_CODES.TOKEN_EXPIRED) {
        return authService.refresh().pipe(
          switchMap((tokens) => next(withAuth(req, tokens.accessToken))),
          catchError((refreshErr: unknown) => {
            authService.logout(true, true);
            return throwError(() => refreshErr);
          }),
        );
      }

      // Every other 401 (TOKEN_INVALID, UNAUTHORIZED, or codeless) is unrecoverable.
      authService.logout(true, true);
      return throwError(() => err);
    }),
  );
};
